/**
 * The hub: one list of every game, solo and online, each with its leaderboard.
 *
 * Three routes, and the whole structure follows from what a game IS rather than
 * from where its code lives:
 *
 *   /            every game, with your rank on each
 *   /g/<id>      one game: its leaderboard, and the button that starts it
 *   /g/<id>/…    solo games are played right here, in the same frame
 *
 * An online game's button hands off to /arena, a separate document. That is on
 * purpose: the arena loads a renderer and a shared simulation before it can
 * draw a frame, and making the hub carry that cost would mean waiting for a
 * 300KB download to find out who is winning at Snake. The handoff is one page
 * load, at the only moment a player has already decided to commit.
 */
import { GAMES, CATEGORIES, entryById, type CatalogueEntry } from './catalogue.js';
import { DEFAULT_LEVEL, LEVELS, LEVEL_BLURB, LEVEL_LABEL, type Level } from '@arena/games';

import {
  cachedIdentity,
  chooseLevel,
  chosenLevel,
  createTransferCode,
  claimTransferCode,
  fetchBoard,
  fetchOpenLobbies,
  fetchSummary,
  identify,
  migrateLocalBests,
  rankFor,
  submitScore,
  type OpenLobby,
  type SummaryRow,
} from './identity.js';
import { h } from './kit.js';
import { renderShop } from './shop.js';
import './shell.css';

/** Replaced at build time by vite. 'dev' when running from source. */
declare const __BUILD__: string;
const BUILD_ID: string = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';

const root = document.getElementById('shell-root')!;

let teardown: (() => void) | null = null;
let summary = new Map<string, SummaryRow>();

// ------------------------------------------------------------------ helpers

function formatValue(game: CatalogueEntry, value: number): string {
  const rounded = Number.isInteger(value) ? value : Math.round(value * 100) / 100;
  // Rated games show a bare number: "1042 points" reads as a score, and a
  // rating is not one.
  if (game.kind === 'online') return String(rounded);
  return `${rounded} ${game.unit}`.trim();
}

/**
 * The URL of the arena, stamped with this build.
 *
 * The stamp is cache busting, and it exists because of a real incident: the
 * shells were matched by NAME when deciding cache headers, a rename dropped
 * arena.html into the one year immutable bucket meant for hashed assets, and
 * every browser that opened the arena in that window cached the shell for a
 * year. `immutable` means the browser will not even revalidate, so fixing the
 * header on the server cannot reach them. A URL they have never requested can.
 *
 * Keeping it permanently, rather than as a one off, means the same mistake can
 * never strand anybody again: every deploy hands out URLs nobody has cached.
 */
function arenaUrl(params: Record<string, string>): string {
  const query = new URLSearchParams({ ...params, b: BUILD_ID });
  return `/arena?${query.toString()}`;
}

function link(href: string): (ev: MouseEvent) => void {
  return (ev: MouseEvent) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
    ev.preventDefault();
    navigate(href);
  };
}

function chrome(): HTMLElement {
  const header = h('header', 'ac-header');

  const brand = h('a', 'ac-brand') as HTMLAnchorElement;
  brand.href = '/';
  brand.append(h('span', 'ac-brand-mark', '\u{1F579}'));
  const name = h('span', 'ac-brand-name');
  // Named for the domain it lives on, play.gameoflife.link, so the thing you
  // type and the thing you land on are the same name.
  name.append(h('span', 'ac-brand-lead', 'Game of '), h('span', 'ac-brand-accent', 'Life'));
  brand.append(name);
  brand.addEventListener('click', link('/'));

  const nav = h('nav', 'ac-nav');
  const shop = h('a', 'ac-nav-item ac-shop-link') as HTMLAnchorElement;
  shop.href = '/skins';
  shop.append(h('span', 'ac-ruby', '♦'), document.createTextNode(' Apparences'));
  shop.addEventListener('click', link('/skins'));
  const who = cachedIdentity();
  const me = h('button', 'ac-nav-item ac-me', who?.name || 'Ton pseudo');
  me.addEventListener('click', openProfile);
  nav.append(shop, me);

  header.append(brand, nav);
  return header;
}

// ------------------------------------------------------------------ profile

/**
 * Name and device transfer.
 *
 * The player id is never shown. It is a bearer credential, so putting it on
 * screen invites somebody to share it, and sharing it hands over your scores.
 * The transfer code exists precisely so that moving devices never requires
 * touching the id itself.
 */
function openProfile(): void {
  const back = h('div', 'ac-modal-back');
  const box = h('div', 'ac-modal');

  box.append(h('h3', '', 'Ton pseudo'));
  const input = h('input', 'ac-search') as HTMLInputElement;
  input.value = cachedIdentity()?.name ?? '';
  input.maxLength = 14;
  input.placeholder = 'Pseudo';
  input.style.width = '100%';
  box.append(input);

  const save = h('button', 'ac-primary', 'Enregistrer');
  save.addEventListener('click', async () => {
    save.textContent = '...';
    await identify(input.value.trim() || 'Joueur');
    close();
    route();
  });

  box.append(
    save,
    h('hr', 'ac-rule'),
    h('h3', '', 'Jouer sur un autre appareil'),
    h(
      'p',
      'ac-note',
      'Tes scores sont lies a cet appareil. Génère un code, puis saisis le sur l’autre appareil pour y retrouver ton classement.',
    ),
  );

  const codeRow = h('div', 'ac-row');
  const codeOut = h('code', 'ac-code', '········');
  const make = h('button', 'ac-ghost', 'Génèrer un code');
  make.addEventListener('click', async () => {
    make.textContent = '...';
    const made = await createTransferCode();
    codeOut.textContent = made ? made.code : 'échec';
    make.textContent = made ? 'Valable 15 min' : 'Réessayer';
  });
  codeRow.append(codeOut, make);

  const claimRow = h('div', 'ac-row');
  const claimInput = h('input', 'ac-search') as HTMLInputElement;
  claimInput.placeholder = 'Code recu';
  claimInput.maxLength = 12;
  const claim = h('button', 'ac-ghost', 'Utiliser');
  claim.addEventListener('click', async () => {
    const code = claimInput.value.trim();
    if (!code) return;
    // This replaces who this device is, so it is worth one deliberate pause.
    if (!window.confirm('Remplacer l’identité de cet appareil par celle du code ?')) return;
    claim.textContent = '...';
    const got = await claimTransferCode(code);
    if (!got) {
      claim.textContent = 'Code invalide';
      return;
    }
    close();
    summary = await fetchSummary();
    route();
  });
  claimRow.append(claimInput, claim);

  box.append(codeRow, claimRow);

  const shut = h('button', 'ac-ghost ac-close', 'Fermer');
  shut.addEventListener('click', () => close());
  box.append(shut);

  const close = (): void => back.remove();
  back.addEventListener('click', (ev) => {
    if (ev.target === back) close();
  });
  back.append(box);
  document.body.append(back);
  input.focus();
}

// ---------------------------------------------------------------------- hub

function renderHub(): void {
  const page = h('div', 'ac-page');
  page.append(chrome());

  const hero = h('section', 'ac-hero');
  const title = h('h1', 'ac-hero-title');
  title.append(
    document.createTextNode('Bienvenue sur '),
    h('span', 'ac-cyan', 'Game of'),
    document.createTextNode(' '),
    h('span', 'ac-magenta', 'Life'),
  );
  hero.append(
    title,
    h(
      'p',
      'ac-hero-sub',
      `${GAMES.length} jeux, seul ou a plusieurs, avec un classement pour chacun. Sans compte, sans installation.`,
    ),
  );
  page.append(hero);

  const tools = h('div', 'ac-tools');
  const search = h('input', 'ac-search') as HTMLInputElement;
  search.type = 'search';
  search.placeholder = 'Rechercher un jeu';
  search.autocomplete = 'off';
  tools.append(search);

  const chips = h('div', 'ac-chips');
  let active = 'Tous';
  const grid = h('div', 'ac-grid');

  const paint = (): void => {
    const q = search.value.trim().toLowerCase();
    grid.replaceChildren();
    const shown = GAMES.filter((g) => {
      if (active !== 'Tous' && g.category !== active) return false;
      if (!q) return true;
      return (
        g.title.toLowerCase().includes(q) ||
        g.blurb.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q)
      );
    });
    for (const g of shown) grid.append(card(g));
    if (!shown.length) grid.append(h('p', 'ac-empty', 'Aucun jeu ne correspond.'));
  };

  for (const c of ['Tous', ...CATEGORIES]) {
    const chip = h('button', 'ac-chip' + (c === 'Tous' ? ' is-active' : ''), c);
    chip.addEventListener('click', () => {
      active = c;
      for (const other of chips.children) other.classList.remove('is-active');
      chip.classList.add('is-active');
      paint();
    });
    chips.append(chip);
  }
  tools.append(chips);
  page.append(tools, grid);
  paint();
  search.addEventListener('input', paint);

  page.append(h('footer', 'ac-footer', 'Game of Life. Aucun compte, aucune installation.'));
  swap(page);
}

function card(game: CatalogueEntry): HTMLElement {
  const a = h('a', 'ac-card') as HTMLAnchorElement;
  a.href = `/g/${game.id}`;
  a.addEventListener('click', link(a.href));
  if (game.kind === 'online') a.classList.add('is-online');

  const top = h('div', 'ac-card-top');
  const tile = h('div', 'ac-tile', game.icon);
  tile.style.setProperty('--tile', game.tint);
  // The card takes its own hue for the top rule and the bloom behind the tile.
  a.style.setProperty('--tile-a', game.hue);
  top.append(tile);
  const tags = h('div', 'ac-tags');
  if (game.kind === 'online') tags.append(h('span', 'ac-tag-online', 'Multijoueur'));
  // Which board this card is showing, when that is a thing you can change. The
  // numbers underneath belong to one difficulty, so the card has to say which.
  if (game.levels && game.kind === 'solo') {
    tags.append(h('span', 'ac-diff', LEVEL_LABEL[chosenLevel(game.id)]));
  } else {
    tags.append(h('span', 'ac-diff', game.difficulty));
  }
  top.append(tags);

  const row = summary.get(game.id);
  const foot = h('div', 'ac-card-foot');
  // The leader is the reason to look, and your own rank is the reason to play,
  // so both go on the card rather than being hidden a click away.
  foot.append(
    h(
      'span',
      'ac-leader',
      row?.leader ? `1er ${row.leader.name} · ${formatValue(game, row.leader.value)}` : 'aucun score',
    ),
  );
  const mine = row?.me ?? null;
  foot.append(
    h('span', 'ac-best' + (mine ? ' is-you' : ''), mine ? `toi ${mine.rank}e` : 'jamais joué'),
  );

  a.append(top, h('h3', 'ac-card-title', game.title), h('p', 'ac-card-blurb', game.blurb), foot);
  return a;
}

// -------------------------------------------------------------- game page

/**
 * The three difficulty buttons.
 *
 * On the game page rather than inside the game, and deliberately so: a level is
 * chosen before you start and fixed for the run. Being able to drop to easy the
 * moment a run got hard, and still post it to the board you started on, would
 * make every board meaningless.
 *
 * The choice is remembered per game, so the child who wants Snake easy and
 * Motus hard is not re-choosing every time.
 */
function levelPicker(game: CatalogueEntry, onChange: (level: Level) => void): HTMLElement {
  const wrap = h('div', 'ac-levels');
  wrap.append(h('span', 'ac-levels-label', 'Difficulté'));
  const row = h('div', 'ac-levels-row');

  const note = h('p', 'ac-note', '');
  const buttons = new Map<Level, HTMLElement>();

  const paint = (active: Level): void => {
    for (const [level, button] of buttons) {
      button.classList.toggle('is-on', level === active);
      button.setAttribute('aria-pressed', String(level === active));
    }
    note.textContent = LEVEL_BLURB[active];
  };

  for (const level of LEVELS) {
    const button = h('button', 'ac-level', LEVEL_LABEL[level]);
    button.title = LEVEL_BLURB[level];
    button.addEventListener('click', () => {
      chooseLevel(game.id, level);
      paint(level);
      onChange(level);
    });
    buttons.set(level, button);
    row.append(button);
  }

  wrap.append(row, note);
  paint(chosenLevel(game.id));
  return wrap;
}

function renderGamePage(game: CatalogueEntry): void {
  const page = h('div', 'ac-page');
  page.append(chrome());

  const bar = h('div', 'ac-gamebar');
  const back = h('button', 'ac-back', '← Tous les jeux');
  back.addEventListener('click', () => navigate('/'));

  const titles = h('div', 'ac-gametitles');
  const heading = h('h2', '');
  heading.append(document.createTextNode(game.title));
  if (game.kind === 'online') heading.append(h('span', 'ac-tag-online', 'Multijoueur'));
  titles.append(heading, h('p', '', game.how));
  bar.append(back, titles);
  page.append(bar);

  const columns = h('div', 'ac-columns');

  // Left: how you start it. Right: the board.
  const action = h('div', 'ac-panel');
  /*
   * Deferred until after swap().
   *
   * The watcher registers itself as the page's teardown, and swap() runs the
   * previous teardown before installing the new page. Starting the poll while
   * building meant swap() immediately stopped it, so the list stayed empty
   * however many lobbies were open.
   */
  let startWatching: (() => void) | null = null;

  if (game.kind === 'online') {
    action.append(h('h3', '', 'Jouer a plusieurs'));
    const create = h('button', 'ac-primary', 'Créer un salon');

    // Public by default. The whole point of the list below is that somebody
    // with nobody to play with can still find a game, and a default of private
    // would leave it permanently empty.
    const privacy = h('label', 'ac-check');
    const privateBox = h('input') as HTMLInputElement;
    privateBox.type = 'checkbox';
    privacy.append(privateBox, h('span', '', 'Salon privé, accessible par code uniquement'));

    create.addEventListener('click', () => {
      window.location.href = arenaUrl({
        mode: game.id,
        create: '1',
        level: chosenLevel(game.id),
        ...(privateBox.checked ? { private: '1' } : {}),
      });
    });

    const joinRow = h('div', 'ac-row');
    const code = h('input', 'ac-search') as HTMLInputElement;
    code.placeholder = 'CODE';
    code.maxLength = 4;
    code.autocapitalize = 'characters';
    const join = h('button', 'ac-ghost', 'Rejoindre');
    const go = (): void => {
      const value = code.value.trim().toUpperCase();
      if (value.length < 4) {
        code.focus();
        return;
      }
      window.location.href = arenaUrl({ mode: game.id, code: value });
    };
    join.addEventListener('click', go);
    code.addEventListener('keydown', (ev) => {
      if ((ev as KeyboardEvent).key === 'Enter') go();
    });
    joinRow.append(code, join);

    const openBox = h('div', 'ac-open');
    /*
     * Online difficulty is the strength of the AI, and nothing else: same
     * arena, same weapon, same scoring, one leaderboard. A rating already
     * accounts for who you beat, and a match padded with bots moves nobody's
     * rating, so there is nothing an easy setting could inflate.
     */
    const botLevel = levelPicker(game, () => {});
    const botLabel = botLevel.querySelector('.ac-levels-label');
    if (botLabel) botLabel.textContent = 'Niveau des adversaires IA';
    action.append(
      botLevel,
      create,
      privacy,
      h('div', 'ac-or', 'ou rejoins un salon'),
      openBox,
      joinRow,
      h(
        'p',
        'ac-note',
        'Le classement ne compte que les parties avec au moins deux joueurs humains.',
      ),
    );
    startWatching = () => watchOpenLobbies(game, openBox);
  } else {
    action.append(h('h3', '', 'Jouer'));
    const play = h('button', 'ac-primary', 'Lancer la partie');
    play.addEventListener('click', () => navigate(`/g/${game.id}/play`));
    action.append(play, h('p', 'ac-note', game.blurb));
  }
  columns.append(action);

  const boardPanel = h('div', 'ac-panel');
  const boardHead = h('h3', '', 'Classement');
  boardPanel.append(boardHead);
  const boardBody = h('div', '', 'chargement...');
  boardBody.className = 'ac-boardbody';

  /*
   * Each difficulty has its own board, so the picker sits with the board and
   * repaints it. Putting it here rather than beside the play button is the
   * point: you can see what you are choosing to be ranked against before you
   * choose it.
   */
  /*
   * Solo only. An online game has one board however good the bots were, so a
   * picker beside it would promise a choice that does not exist; its own picker
   * is up with the create button, where it belongs, since it changes who you
   * play rather than where you are ranked.
   */
  if (game.levels && game.kind === 'solo') {
    action.append(
      levelPicker(game, (level) => {
        void paintBoard(game, boardBody, level);
      }),
    );
    boardHead.append(h('span', 'ac-level-tag', LEVEL_LABEL[chosenLevel(game.id)]));
  }

  boardPanel.append(boardBody);
  columns.append(boardPanel);

  page.append(columns);
  swap(page);
  startWatching?.();
  void paintBoard(game, boardBody, game.kind === 'solo' ? chosenLevel(game.id) : DEFAULT_LEVEL);
}

/**
 * Keep the open lobby list fresh while this page is on screen.
 *
 * Polled rather than pushed: the hub holds no websocket, and opening one just
 * to watch a list would cost more than it saves. Six seconds is slow enough to
 * be invisible on the server and fast enough that a lobby somebody opened while
 * you were reading appears before you give up.
 *
 * Stops when the tab is hidden and when you navigate away. The second matters
 * most: the shell reuses one container, so a timer left running would keep
 * writing into a detached node forever. It is registered with the same teardown
 * the games use, so leaving the page cannot leak it.
 */
function watchOpenLobbies(game: CatalogueEntry, into: HTMLElement): void {
  let stopped = false;

  const paint = (lobbies: OpenLobby[]): void => {
    into.replaceChildren();
    if (!lobbies.length) {
      into.append(
        h('p', 'ac-note', 'Aucun salon ouvert pour l’instant. Cree en un, on te rejoindra.'),
      );
      return;
    }
    const list = h('div', 'ac-lobbies');
    for (const lobby of lobbies) {
      const row = h('button', 'ac-lobby');
      const left = h('span', 'ac-lobby-left');
      left.append(
        h('strong', '', lobby.host),
        h('span', 'ac-lobby-sub', `${lobby.players} / ${lobby.capacity} joueurs`),
      );
      row.append(left, h('span', 'ac-lobby-code', lobby.code));
      row.addEventListener('click', () => {
        window.location.href = arenaUrl({ mode: game.id, code: lobby.code });
      });
      list.append(row);
    }
    into.append(list);
  };

  const refresh = async (): Promise<void> => {
    if (stopped) return;
    const lobbies = await fetchOpenLobbies(game.id);
    if (!stopped) paint(lobbies);
  };

  /*
   * The FIRST fetch always happens, hidden tab or not.
   *
   * Only the repeat is skipped while hidden. Guarding both meant a page opened
   * in a background tab, which is what a middle click or a restored session
   * produces, showed an empty list until it was focused and six seconds had
   * passed. Coming back to the tab refreshes immediately rather than waiting
   * out the interval, because a stale list is exactly what you are looking at
   * when you return.
   */
  const tick = async (): Promise<void> => {
    if (stopped) return;
    if (document.visibilityState === 'visible') await refresh();
    if (!stopped) timer = window.setTimeout(() => void tick(), 6_000);
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void refresh();
  };
  document.addEventListener('visibilitychange', onVisible);

  let timer = window.setTimeout(() => void tick(), 6_000);
  void refresh();

  teardown = () => {
    stopped = true;
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

async function paintBoard(
  game: CatalogueEntry,
  into: HTMLElement,
  level: Level = DEFAULT_LEVEL,
): Promise<void> {
  const { rows, me } = await fetchBoard(game.id, 20, level);
  const tag = into.parentElement?.querySelector('.ac-level-tag');
  if (tag) tag.textContent = LEVEL_LABEL[level];
  into.replaceChildren();
  if (!rows.length) {
    into.append(
      h('p', 'ac-note', 'Personne n’a encore joué. La première place est libre.'),
    );
    return;
  }
  const mineId = cachedIdentity()?.id;
  const table = h('ol', 'ac-board-list');
  for (const row of rows) {
    const li = h('li', 'ac-board-row' + (row.playerId === mineId ? ' is-you' : ''));
    li.append(
      h('span', 'ac-board-rank', `${row.rank}`),
      h('span', 'ac-board-name', row.name),
      h('span', 'ac-board-value', formatValue(game, row.value)),
    );
    table.append(li);
  }
  into.append(table);
  if (me) {
    const mine = h('div', 'ac-board-me');
    mine.append(
      h('span', 'ac-board-rank', `${me.rank}`),
      h('span', 'ac-board-name', me.name),
      h('span', 'ac-board-value', formatValue(game, me.value)),
    );
    into.append(h('p', 'ac-note', 'Ta position'), mine);
  }
}

// ------------------------------------------------------------- playing solo

function renderPlay(game: CatalogueEntry): void {
  if (!game.mount) {
    navigate(`/g/${game.id}`);
    return;
  }

  const page = h('div', 'ac-page ac-page-game');
  page.append(chrome());

  const bar = h('div', 'ac-gamebar');
  // Every game has the same way out, in the same place. Online games get the
  // same control in the arena, so quitting never depends on knowing where you
  // happen to be.
  const back = h('button', 'ac-back', '← Quitter');
  back.addEventListener('click', () => navigate(`/g/${game.id}`));

  // The difficulty is fixed for the whole run and chosen on the game page. It
  // cannot be changed from inside a game: that would let anybody drop to easy
  // the moment a run got hard and still post it to the board they started on.
  const level = chosenLevel(game.id);

  const titles = h('div', 'ac-gametitles');
  const heading = h('h2', '', game.title);
  // Say which difficulty is being played, since the record beside it and the
  // board it will land on both belong to this one and not to the others.
  if (game.levels) heading.append(h('span', 'ac-level-tag', LEVEL_LABEL[level]));
  titles.append(heading, h('p', '', game.how));

  const bestBox = h('div', 'ac-bestbox');
  const bestValue = h('strong', '', '--');
  const rankValue = h('span', 'ac-rankline', '');
  bestBox.append(h('span', '', 'Record'), bestValue, rankValue);

  const paintBest = (): void => {
    const row = rankFor(game.id, level);
    bestValue.textContent = row ? formatValue(game, row.value) : '--';
    rankValue.textContent = row ? `${row.rank}e` : '';
  };
  paintBest();

  bar.append(back, titles, bestBox);
  const stage = h('div', 'ac-stage');
  page.append(bar, stage);
  swap(page);

  teardown = game.mount(stage, {
    submit(score: number) {
      // Fire and forget. A game must never wait on the network to tell you
      // that you beat your record.
      void submitScore(game.id, score, level).then((result) => {
        if (!result) return;
        paintBest();
        if (result.improved) {
          bestBox.classList.remove('is-new');
          void bestBox.offsetWidth;
          bestBox.classList.add('is-new');
          // Say what you just earned, where you earned it. A reward that only
          // appears as a larger total on another page is a reward nobody
          // notices, and this one was silently paying zero for a first record.
          if (result.earned > 0) rubyToast(result.earned, result.rubies);
        }
      });
    },
    level,
    best: () => rankFor(game.id, level)?.value ?? null,
    exit: () => navigate(`/g/${game.id}`),
  });
}

function renderSkins(): void {
  const page = h('div', 'ac-page');
  page.append(chrome());

  const bar = h('div', 'ac-gamebar');
  const back = h('button', 'ac-back', '← Tous les jeux');
  back.addEventListener('click', () => navigate('/'));
  const titles = h('div', 'ac-gametitles');
  titles.append(
    h('h2', '', 'Apparences'),
    h('p', '', 'Toutes les apparences sont à débloquer. Joue pour gagner des rubis.'),
  );
  bar.append(back, titles);
  page.append(bar);

  const panel = h('div', 'ac-panel');
  page.append(panel);
  swap(page);
  // Repaint the header on a change so the ruby count is never stale.
  renderShop(panel, () => {
    const nav = document.querySelector('.ac-header');
    if (nav) nav.replaceWith(chrome());
  });
}

/** A small, brief note that rubies just arrived. */
function rubyToast(earned: number, total: number): void {
  const toast = h('div', 'ac-ruby-toast');
  toast.append(
    h('span', 'ac-ruby', '♦'),
    h('strong', '', `+${earned}`),
    h('span', 'ac-ruby-total', `${total} au total`),
  );
  document.body.append(toast);
  window.setTimeout(() => toast.classList.add('is-out'), 2200);
  window.setTimeout(() => toast.remove(), 2600);
}

function renderMissing(): void {
  const page = h('div', 'ac-page');
  page.append(chrome());
  const box = h('div', 'ac-missing');
  box.append(h('h2', '', 'Ce jeu n’existe pas'));
  const back = h('button', 'ac-primary', 'Retour aux jeux');
  back.addEventListener('click', () => navigate('/'));
  box.append(back);
  page.append(box);
  swap(page);
}

/**
 * Replace the page, tearing down whatever was running first.
 *
 * The order matters: one container is reused for every game, so a game whose
 * loop is still going would keep drawing into a detached node and hold its
 * timers and listeners alive forever.
 */
function swap(page: HTMLElement): void {
  if (teardown) {
    try {
      teardown();
    } catch {
      // A broken teardown must not block navigation.
    }
    teardown = null;
  }
  root.replaceChildren(page);
  window.scrollTo(0, 0);
}

// ------------------------------------------------------------------- router

function route(): void {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  if (path === '/') {
    document.title = 'Game of Life';
    renderHub();
    return;
  }

  const parts = path.split('/').filter(Boolean);
  if (parts[0] === 'skins') {
    document.title = 'Apparences | Game of Life';
    renderSkins();
    return;
  }
  if (parts[0] !== 'g' || !parts[1]) {
    renderMissing();
    return;
  }
  const game = entryById(parts[1]);
  if (!game) {
    renderMissing();
    return;
  }

  document.title = `${game.title} | Game of Life`;
  if (parts[2] === 'play') renderPlay(game);
  else renderGamePage(game);
}

function navigate(href: string): void {
  if (href === window.location.pathname) return;
  history.pushState(null, '', href);
  route();
}

window.addEventListener('popstate', route);

/**
 * Boot.
 *
 * The page is drawn BEFORE the network is consulted, so a sleeping server or a
 * flaky connection costs a moment of missing ranks rather than a blank screen.
 * Everything about the leaderboard is an enhancement of a site that already
 * works without it.
 */
route();
void (async () => {
  await identify();
  await migrateLocalBests();
  summary = await fetchSummary();
  // Repaint only if the hub is still what is on screen: the player may have
  // opened a game while this was in flight, and yanking it away would be rude.
  if (window.location.pathname === '/') route();
})();
